import React, { useDeferredValue, useMemo, useState } from "react";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { OrgHomeScreenProps } from "../OrgHomeNavigation";
import { FormattedMessage, useIntl } from "react-intl";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useSubscription,
} from "react-relay";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { WorkspaceTodosList_workspace$key } from "./__generated__/WorkspaceTodosList_workspace.graphql";
import { WorkspaceTodosQuery } from "./__generated__/WorkspaceTodosQuery.graphql";
import { OrgSearchInput } from "../OrgSearchInput";

import { FlatList } from "react-native";
import { useOrgListStyleProps } from "../useOrgHomeDetailContainerStyle";
import { AHEmptyScreen, AHSeparator, AHText, Box, useTheme } from "arkh";
import { WorkspaceTodoListItem } from "./WorkspaceTodoListItem";
import {
  WorkspaceTodoHeader,
  WorkspaceTodoSortKey,
} from "./WorkspaceTodoHeader";
import cuid from "cuid";
import { WorkspaceTodoDetailScreen } from "./WorkspaceTodoDetail/WorkspaceTodoDetailScreen";
import { WorkspaceEmptyListItem } from "../WorkspaceEmptyListItem";
import { WorkspaceTodosSubscription } from "./__generated__/WorkspaceTodosSubscription.graphql";
import { Image } from "expo-image";
import { OrgButton } from "../OrgButton";
import {
  WorkspaceNavigationProp,
  WorkspaceTodoStack,
} from "./WorkspaceTodoStack";
import { WorkspaceTodosArchived } from "./WorkspaceTodosArchived/WorkspaceTodosArchived";
import { useWorkspaceId, WorkspaceWritable } from "../workspaceId";
import { WorkspaceDetailNavigationHeader } from "../WorkspaceDetailNavigationHeader";

function sortWithFallback<T extends { title: string; id: string }>(
  fun: (a: T, b: T) => number
) {
  return (a: T, b: T) => {
    const result = fun(a, b);
    if (result !== 0) {
      return result;
    }
    const titleValue = b.title.localeCompare(a.title);
    if (titleValue !== 0) {
      return titleValue;
    }

    return b.id.localeCompare(a.id);
  };
}

function InnerWorkspaceTodos({
  fetchKey,
  ...props
}: {
  fetchKey: string;
  navigation: WorkspaceNavigationProp<"WorkspaceTodosMain">;
}) {
  const workspaceId = useWorkspaceId();

  const data = useLazyLoadQuery<WorkspaceTodosQuery>(
    graphql`
      query WorkspaceTodosQuery($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          ...WorkspaceTodosList_workspace
        }
      }
    `,
    {
      workspaceId,
    },
    { fetchKey, fetchPolicy: "store-and-network" }
  );

  return data.workspace ? (
    <List
      workspace={data.workspace}
      openTodoCreate={() => {
        props.navigation.navigateDeprecated("WorkspaceTodoEdit", {
          workspaceId,
          id: cuid(),
          create: true,
        });
      }}
      openTodoWorkspaceEdit={(id) => {
        props.navigation.navigateDeprecated("WorkspaceTodoEdit", {
          workspaceId,
          id,
        });
      }}
      openTodoWorkspace={(id) => {
        props.navigation.navigateDeprecated("WorkspaceTodoDetail", {
          todoWorkspaceId: id,
        });
      }}
      openArchived={() => {
        props.navigation.navigateDeprecated("WorkspaceTodosArchived", {
          workspaceId,
        });
      }}
    />
  ) : null;
}

function WorkspaceTodosTitle() {
  const intl_tokens = useIntlTokens();
  return intl_tokens.Todos;
}

function WorkspaceTodosSubTitle() {
  return (
    <FormattedMessage
      description="WorkspaceTodos subtitle"
      defaultMessage="Create and assign tasks to selected or all units within your workspace, customize with instructions, priority, required response and deadlines, and track progress in real time. Start drafting and publish when ready."
    />
  );
}
function List(props: {
  workspace: WorkspaceTodosList_workspace$key;
  openTodoWorkspace: (todoId: string) => void;
  openTodoWorkspaceEdit: (todoId: string) => void;
  openTodoCreate: () => void;
  openArchived: () => void;
}) {
  const [_search, setSearch] = React.useState("");
  const search = useDeferredValue(_search);

  const workspace = useFragment(
    graphql`
      fragment WorkspaceTodosList_workspace on Workspace {
        id
        topics {
          __typename
        }
        activeTodos {
          id
          title
          body

          createdAtDate {
            isoString
          }
          scheduledPublishAt {
            isoString
          }
          publishedAtDate {
            isoString
          }
          expireAtDate {
            isoString
          }
          topic {
            id
            name
          }

          status
          todoCount(workspaceId: $workspaceId)
          todosCompletedCount(workspaceId: $workspaceId)
          priorityOrder
          ...WorkspaceTodoListItem_todo
        }
      }
    `,
    props.workspace
  );

  useSubscription<WorkspaceTodosSubscription>(
    useMemo(
      () => ({
        variables: {
          workspaceId: workspace.id,
        },
        subscription: graphql`
          subscription WorkspaceTodosSubscription($workspaceId: ID!) {
            todoWorkspaceChanged(workspaceId: $workspaceId) {
              id
              title
              body
              createdAtDate {
                isoString
              }
              publishedAtDate {
                isoString
              }
              expireAtDate {
                isoString
              }
              status
              todoCount(workspaceId: $workspaceId)
              todosCompletedCount(workspaceId: $workspaceId)
              priorityOrder
              ...WorkspaceTodoListItem_todo
            }
          }
        `,
      }),
      [workspace.id]
    )
  );

  const listStyle = useOrgListStyleProps();
  const filteredTodos = useMemo(() => {
    if (search.length > 0) {
      const s = search.toLocaleLowerCase();
      return workspace.activeTodos.filter(
        (item) =>
          item?.id &&
          (item.title.toLocaleLowerCase().includes(s) ||
            item.body?.toLowerCase().includes(s))
      );
    }
    return workspace.activeTodos.filter((item) => item?.id);
  }, [search, workspace.activeTodos]);

  const [order, setOrder] = useState<{
    column: WorkspaceTodoSortKey;
    order: "ASC" | "DESC";
  }>({
    column: "published",
    order: "DESC",
  });

  type Todo = (typeof workspace.activeTodos)[number];

  const sortFunction = useMemo(() => {
    switch (order.column) {
      case "priority":
        return (a: Todo, b: Todo) => {
          if (a.priorityOrder === b.priorityOrder) {
            return b.title.localeCompare(a.title);
          }
          return a.priorityOrder - b.priorityOrder;
        };
      case "description":
        return (a: Todo, b: Todo) => b.title.localeCompare(a.title);
      case "deadline":
        return (a: Todo, b: Todo) =>
          (b.expireAtDate?.isoString ?? "").localeCompare(
            a.expireAtDate?.isoString ?? ""
          );
      case "published":
        return (a: Todo, b: Todo) => {
          const aPublishD =
            a.publishedAtDate?.isoString ?? a.scheduledPublishAt?.isoString;
          const bPublishD =
            b.publishedAtDate?.isoString ?? b.scheduledPublishAt?.isoString;
          if (aPublishD === bPublishD) {
            if (a.createdAtDate.isoString === b.createdAtDate.isoString) {
              return a.title.localeCompare(b.title);
            }
            return a.createdAtDate.isoString.localeCompare(
              b.createdAtDate.isoString
            );
          }
          if (aPublishD == null) {
            return 1;
          }
          if (bPublishD == null) {
            return -1;
          }
          return aPublishD.localeCompare(bPublishD);
        };
      case "status":
        return (a: Todo, b: Todo) => {
          if (b.status === "DRAFT") {
            return -1;
          }
          if (a.status === "DRAFT") {
            return 1;
          }
          return (
            a.todoCount / a.todosCompletedCount -
            b.todoCount / b.todosCompletedCount
          );
        };
      case "filter":
        return (a: Todo, b: Todo) => b.status.localeCompare(a.status);
      case "topic":
        return (a: Todo, b: Todo) => {
          if (a.topic?.id === b.topic?.id) {
            return a.title.localeCompare(b.title);
          }
          return a.topic?.name.localeCompare(b.topic?.name ?? "") ?? 0;
        };
    }
  }, [order.column]);

  const todos = useMemo(() => {
    const sf = sortWithFallback(sortFunction);
    if (order.order === "DESC") {
      return filteredTodos.slice().sort((a, b) => -sf(a, b));
    }

    return filteredTodos.slice().sort(sf);
  }, [filteredTodos, order.order, sortFunction]);

  if (workspace.activeTodos.length === 0) {
    return (
      <AHEmptyScreen
        title={<WorkspaceTodosTitle />}
        subtitle={<WorkspaceTodosSubTitle />}
        illustration={
          <Box style={{ flex: 1 }}>
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./Todo.png")}
            />
          </Box>
        }
      />
    );
  }

  const showTopics = workspace.topics ? workspace.topics.length > 0 : false;

  return (
    <FlatList
      {...listStyle}
      data={todos}
      keyboardShouldPersistTaps="handled"
      renderItem={({ item, index }) => {
        return (
          <WorkspaceTodoListItem
            workspaceTodo={item}
            isLast={index + 1 === todos.length}
            onPress={props.openTodoWorkspace}
            onPressEdit={props.openTodoWorkspaceEdit}
            showTopics={showTopics}
          />
        );
      }}
      ListHeaderComponent={
        <Box color="surface-background-light" spaceTop="medium">
          <OrgSearchInput value={_search} onChangeText={setSearch} />
          <WorkspaceTodoHeader
            order={order}
            onOrderChange={setOrder}
            showTopics={showTopics}
          />
          <AHSeparator />
        </Box>
      }
      ListEmptyComponent={WorkspaceEmptyListItem}
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
    />
  );
}

function WorkspaceTodosMainScreen(props: {
  navigation: WorkspaceNavigationProp<"WorkspaceTodosMain">;
}) {
  const intl_tokens = useIntlTokens();
  const workspaceId = useWorkspaceId();
  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <AHText type="subtitle">
            <WorkspaceTodosTitle />
          </AHText>
        }
        right={
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
            spaceRight="medium"
          >
            <OrgButton
              backgroundColor="transparent"
              icon="archive"
              tintColor="text-secondary"
              title={intl_tokens.ShowArchived}
              onPress={() => {
                props.navigation.navigateDeprecated("WorkspaceTodosArchived", {
                  workspaceId,
                });
              }}
            />
            <Box space="medium" />
            <WorkspaceWritable>
              <OrgButton
                backgroundColor="primary"
                tintColor="text-inverted"
                title={intl_tokens.Add}
                icon="add"
                onPress={() => {
                  props.navigation.navigateDeprecated("WorkspaceTodoEdit", {
                    workspaceId,
                    id: cuid(),
                    create: true,
                  });
                }}
              />
            </WorkspaceWritable>
          </Box>
        }
      />
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => <InnerWorkspaceTodos {...props} fetchKey={fetchKey} />}
      </LazyQueryNetworkBoundrary>
    </>
  );
}

export function WorkspaceTodosScreen(
  props: OrgHomeScreenProps<"WorkspaceTodos">
) {
  const theme = useTheme();
  const intl = useIntl();
  return (
    <WorkspaceTodoStack.Navigator>
      <WorkspaceTodoStack.Screen
        name="WorkspaceTodosMain"
        component={WorkspaceTodosMainScreen}
        options={{
          headerShown: false,
          cardStyle: {
            backgroundColor: theme["surface-background-light"],
          },
        }}
      />
      <WorkspaceTodoStack.Screen
        name="WorkspaceTodoDetail"
        component={WorkspaceTodoDetailScreen}
        options={{
          headerShown: false,
          cardStyle: {
            backgroundColor: theme["surface-background-light"],
          },
          headerBackButtonDisplayMode: "default",
          title: "",
        }}
      />
      <WorkspaceTodoStack.Screen
        name="WorkspaceTodosArchived"
        component={WorkspaceTodosArchived}
        options={{
          headerShown: false,
          headerShadowVisible: false,
          headerStyle: {
            backgroundColor: theme["surface-background-light"],
          },
          cardStyle: {
            backgroundColor: theme["surface-background-light"],
          },
          headerBackButtonDisplayMode: "default",

          title: "",
          headerBackTitle: intl.formatMessage({
            defaultMessage: "All Todos",
            description: "WorkspaceTodosOccasion headerBackTitle",
          }),
        }}
      />
    </WorkspaceTodoStack.Navigator>
  );
}
